import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Row, Col, Button, Tag, Space, List } from 'antd';
import { CheckOutlined, RocketOutlined, StarFilled, SendOutlined, CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import './PricingPage.css';

const { Title, Paragraph } = Typography;

const PricingPage: React.FC = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();

  const plans = [
    {
      id: 'basic',
      title: t('landing.pricing.plans.basic.title'),
      price: t('landing.pricing.plans.basic.price'),
      originalPrice: t('landing.pricing.plans.basic.originalPrice'),
      description: t('landing.pricing.plans.basic.description'),
      features: t('landing.pricing.plans.basic.features', { returnObjects: true }) as string[],
      buttonText: t('landing.pricing.plans.basic.button'),
      recommended: false,
    },
    {
      id: 'pro',
      title: t('landing.pricing.plans.pro.title'),
      price: t('landing.pricing.plans.pro.price'),
      originalPrice: t('landing.pricing.plans.pro.originalPrice'),
      description: t('landing.pricing.plans.pro.description'),
      features: t('landing.pricing.plans.pro.features', { returnObjects: true }) as string[],
      buttonText: t('landing.pricing.plans.pro.button'),
      recommended: true,
    },
    {
      id: 'enterprise',
      title: t('landing.pricing.plans.enterprise.title'),
      price: t('landing.pricing.plans.enterprise.price'),
      originalPrice: t('landing.pricing.plans.enterprise.originalPrice'),
      description: t('landing.pricing.plans.enterprise.description'),
      features: t('landing.pricing.plans.enterprise.features', { returnObjects: true }) as string[],
      buttonText: t('landing.pricing.plans.enterprise.button'),
      recommended: false,
    },
  ];

  const onPremisePlan = {
    id: 'onpremise',
    title: t('landing.pricing.plans.onpremise.title'),
    price: t('landing.pricing.plans.onpremise.price'),
    originalPrice: t('landing.pricing.plans.onpremise.originalPrice'),
    description: t('landing.pricing.plans.onpremise.description'),
    features: t('landing.pricing.plans.onpremise.features', { returnObjects: true }) as string[],
    buttonText: t('landing.pricing.plans.onpremise.button'),
    recommended: false,
  };

  const handlePlanSelect = () => {
    navigate('/register');
  };

  return (
    <>
      <SEO
        title="Тарифы QATE | Цены на AI-платформу управления тестированием"
        description="Выберите подходящий тариф QATE для вашей команды. Бесплатный план для старта, Professional для команд, Enterprise для крупных организаций. Прозрачные цены на QA решения."
        keywords="тарифы QATE, цены QA платформы, стоимость тест менеджмента, pricing, QA subscription, test management cost, планы подписки"
        canonicalUrl="/pricing"
        ogTitle="Тарифы QATE | Цены на AI-платформу управления тестированием"
        ogDescription="Выберите подходящий тариф QATE для вашей команды. Бесплатный план для старта, Professional для команд, Enterprise для организаций."
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Тарифы QATE",
          "description": "Планы подписки и цены на AI-платформу управления тестированием QATE",
          "mainEntity": {
            "@type": "Product",
            "name": "QATE Platform",
            "offers": [
              {
                "@type": "Offer",
                "name": "Free Plan",
                "price": "0",
                "priceCurrency": "RUB",
                "priceValidUntil": "2025-12-31",
                "availability": "https://schema.org/InStock",
                "description": "Бесплатный план для начинающих команд"
              },
              {
                "@type": "Offer",
                "name": "Professional",
                "price": "2990",
                "priceCurrency": "RUB", 
                "priceValidUntil": "2025-12-31",
                "availability": "https://schema.org/InStock",
                "description": "Профессиональный план для команд разработки"
              },
              {
                "@type": "Offer",
                "name": "Enterprise",
                "price": "9990",
                "priceCurrency": "RUB",
                "priceValidUntil": "2025-12-31", 
                "availability": "https://schema.org/InStock",
                "description": "Корпоративный план для крупных организаций"
              }
            ]
          }
        }}
      />
      <div className="pricing-page">
        <div className="pricing-container">
          {/* Hero Section */}
          <Row justify="center" align="middle" className="hero-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <div className="alpha-tag">
                <Tag color="blue">{t('landing.pricing.alphaTag')}</Tag>
              </div>
              <Title level={1} className="hero-title">
                {t('landing.pricing.title')}
              </Title>
              <div className="hero-subtitle">
                {t('landing.pricing.subtitle')}
              </div>
              <Paragraph className="hero-description">
                {t('landing.pricing.description')}
              </Paragraph>
              <Paragraph className="alpha-description">
                {t('landing.pricing.alphaDescription')}
              </Paragraph>
            </Col>
          </Row>

          {/* Pricing Plans */}
          <div className="pricing-plans-section">
            {/* Main Plans Grid */}
            <div className="pricing-plans-grid">
              {plans.map((plan, index) => (
                <div
                  key={plan.id}
                  className={`pricing-card ${plan.recommended ? 'recommended' : ''}`}
                >
                  {plan.recommended && (
                    <div className="recommended-badge">
                      {t('landing.pricing.plans.pro.recommended')}
                    </div>
                  )}
                  
                  <div className="pricing-card-header">
                    <div className="pricing-card-title">{plan.title}</div>
                    <div className="pricing-card-price">{plan.price}</div>
                    {plan.originalPrice && (
                      <div className="pricing-card-original-price">
                        {plan.originalPrice}
                      </div>
                    )}
                    <div className="pricing-card-description">{plan.description}</div>
                  </div>

                  <div className="pricing-card-features">
                    <ul>
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>
                          <CheckOutlined />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    className={`pricing-card-button ${plan.recommended ? 'primary' : 'default'}`}
                    block
                    onClick={handlePlanSelect}
                    icon={<RocketOutlined />}
                  >
                    {plan.buttonText}
                  </Button>
                </div>
              ))}
            </div>

            {/* On-Premise Plan */}
            <div className="onpremise-plan-section">
              <div className="onpremise-plan-card">
                <div className="onpremise-plan-header">
                  <div className="onpremise-plan-title">{onPremisePlan.title}</div>
                  <div className="onpremise-plan-price">{onPremisePlan.price}</div>
                  {onPremisePlan.originalPrice && (
                    <div className="onpremise-plan-original-price">
                      {onPremisePlan.originalPrice}
                    </div>
                  )}
                  <div className="onpremise-plan-description">{onPremisePlan.description}</div>
                </div>

                <div className="onpremise-plan-features">
                  <div className="onpremise-features-row">
                    {onPremisePlan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="onpremise-feature-item">
                        <CheckOutlined />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="onpremise-plan-action">
                  <Button
                    className="onpremise-plan-button"
                    size="large"
                    onClick={handlePlanSelect}
                    icon={<RocketOutlined />}
                  >
                    {onPremisePlan.buttonText}
                  </Button>
                </div>
              </div>
            </div>

            {/* Alpha Note */}
            <div className="pricing-note">
              {t('landing.pricing.note')}
            </div>
          </div>

          {/* CTA Section */}
          <div className="pricing-cta-section">
            <Title level={2} className="pricing-cta-title">
              {t('landing.pricing.cta.title')}
            </Title>
            <Paragraph className="pricing-cta-subtitle">
              {t('landing.pricing.cta.subtitle')}
            </Paragraph>
            <Button
              className="pricing-cta-button"
              size="large"
              onClick={handlePlanSelect}
              icon={<RocketOutlined />}
            >
              {t('landing.pricing.cta.button')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage; 